import { useId } from 'react'

/**
 * A labelled input with error association.
 *
 * Labels sit above the field, never as a placeholder. Errors are linked with
 * aria-describedby and carry an icon, so state is never signalled by colour alone.
 */
export function Field({
  label,
  type = 'text',
  value,
  onChange,
  error,
  hint,
  autoComplete,
  required,
  minLength,
  autoFocus,
  disabled,
}: {
  label: string
  type?: string
  value: string
  onChange: (value: string) => void
  error?: string | undefined
  hint?: string | undefined
  autoComplete?: string | undefined
  required?: boolean
  minLength?: number | undefined
  autoFocus?: boolean
  disabled?: boolean
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div style={{ marginBottom: 'var(--spacing-4)' }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 'var(--text-compact)',
          fontWeight: 500,
          marginBottom: 'var(--spacing-2)',
        }}
      >
        {label}
        {required && (
          <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>
            {' '}
            *
          </span>
        )}
      </label>

      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        // Passed through by the caller. Used only on single-purpose forms
        // (sign-in, sign-up), where the form IS the page content and focusing
        // the first field orients rather than disrupts.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        style={{
          width: '100%',
          height: 48,
          padding: '0 var(--spacing-3)',
          background: 'var(--color-surface)',
          border: `1px solid ${error ? 'var(--color-danger)' : 'var(--color-border-strong)'}`,
          borderRadius: 'var(--radius-control)',
          fontSize: 'var(--text-compact)',
          color: 'var(--color-text)',
        }}
      />

      {hint && !error && (
        <p
          id={hintId}
          style={{
            marginTop: 'var(--spacing-2)',
            fontSize: 'var(--text-caption)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {hint}
        </p>
      )}

      {error && (
        <p
          id={errorId}
          style={{
            marginTop: 'var(--spacing-2)',
            fontSize: 'var(--text-caption)',
            color: 'var(--color-danger)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-1)',
          }}
        >
          <span aria-hidden="true">⚠</span>
          {error}
        </p>
      )}
    </div>
  )
}

export function SubmitButton({
  children,
  loading,
  disabled,
}: {
  children: string
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      aria-busy={loading}
      className="pressable"
      style={{
        width: '100%',
        height: 44,
        background: 'var(--color-action)',
        color: 'var(--color-text-on-action)',
        border: 'none',
        borderRadius: 'var(--radius-control)',
        fontSize: 'var(--text-compact)',
        fontWeight: 600,
        opacity: loading || disabled ? 0.6 : 1,
      }}
    >
      {/* The label stays put while loading, so the button does not jump. */}
      {loading ? 'Working…' : children}
    </button>
  )
}

export function FormError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        marginBottom: 'var(--spacing-4)',
        padding: 'var(--spacing-3)',
        background: 'var(--color-danger-subtle)',
        color: 'var(--color-danger)',
        borderRadius: 'var(--radius-control)',
        fontSize: 'var(--text-compact)',
      }}
    >
      {message}
    </div>
  )
}
